import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import useGenerateInvoice from '../hooks/useGenerateInvoice';
import { money as sharedMoney } from '../utils/money';
import { CommissionReviewPanel } from './CommissionAdjustModal'; // item 341
import { useConfirm } from './ConfirmProvider'; // item 1470
import FinalizeInvoiceModal from './FinalizeInvoiceModal'; // item 772 — finalize right here
import SendDocumentButton from './forms/SendDocumentButton'; // item 772 — …and send right here

export interface WizardWorkOrder {
  id: string;
  workOrderNumber?: string;
  customerName?: string;
  retailTotal?: number;
  isSalesOrder?: boolean;
  proposalId?: string | null; // item 1488 — service (no proposal) itemizes by default
}

interface WorkOrderInvoiceWizardProps {
  isOpen: boolean;
  onClose: () => void;
  workOrder?: WizardWorkOrder;
  // item 1583 — "Rebill & write off" (from a voided invoice): same three steps, no send, and the
  // host takes over with the write-off modal once the new invoice is finalized
  writeOff?: boolean;
  onFinalizedInvoice?: (invoiceId: string) => void;
}

const STEPS = ['Create invoice', 'Commissions', 'Finalize'];

// item 327 — stealth-aware, but keep this wizard's em-dash for a MISSING value
const money = (n?: number) => (typeof n === 'number' ? sharedMoney(n) : '—');

// item 230: a guided Work Order → Invoice path, opened when a WO is marked CLOSED
// (mirrors ProposalApprovalWizard). Step 1 generates an editable draft invoice from
// the work order; step 2 points the user to the draft to enter/adjust lines and
// FINALIZE when ready (item 38). The closing chat note is posted by the caller.
const WorkOrderInvoiceWizard: React.FC<WorkOrderInvoiceWizardProps> = ({
  isOpen, onClose, workOrder, writeOff, onFinalizedInvoice,
}) => {
  const { generate, isGenerating } = useGenerateInvoice();
  const history = useHistory();
  const toast = useToast();
  const [step, setStep] = useState(0);
  // item 1334 — the step-2 panel's UNSAVED edits must persist when the user continues:
  // before this, zeroing rates and clicking "Looks right — continue" saved NOTHING (only
  // the panel's own Save split button posted), so the finalize booked standing rates.
  const panelSaveRef = React.useRef<(() => Promise<boolean>) | null>(null);
  const [booksNow, setBooksNow] = React.useState(true); // item 1470
  const confirmDialog = useConfirm(); // item 1470
  const [itemize, setItemize] = useState(false);
  const [newInvoiceId, setNewInvoiceId] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStep(0);
      setItemize(!workOrder?.proposalId); // item 1488 — service invoices itemize by default
      setNewInvoiceId('');
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const label = workOrder?.isSalesOrder ? 'sales order' : 'work order';

  const onGenerate = async () => {
    if (!workOrder) return;
    try {
      const id = await generate({ workOrderId: workOrder.id, itemize });
      setNewInvoiceId(id);
      setStep(1);
    } catch (e: any) {
      const msg = e?.response?.data?.errors?.[0] ?? e?.response?.data ?? 'The invoice could not be generated.';
      toast({ status: 'error', title: 'Generate failed', description: String(msg) });
    }
  };

  // item 772 (Chad) — the deposit and final flows should MATCH: finalize (± send) right from
  // the wizard's last step instead of always bouncing to the invoice form. Opening the draft
  // stays available for line edits.
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [sendSignal, setSendSignal] = useState(0);

  const openDraft = () => {
    onClose();
    history.push(`/invoice/${newInvoiceId}`);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered closeOnOverlayClick={!isGenerating}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={3}>
          <Text>{writeOff ? 'Rebill & write off' : 'Invoice this work order'}</Text>
          <Progress mt={3} borderRadius="full" size="xs" value={((step + 1) / STEPS.length) * 100} />
          <Text mt={2} fontSize="xs" color="gray.500" fontWeight="normal">
            {`Step ${step + 1} of ${STEPS.length} — ${STEPS[step]}`}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody minH="160px" py={5}>
          {step === 0 && (
            <Stack spacing={4}>
              <Text fontSize="sm" color="gray.500">
                {writeOff
                  ? `The voided invoice reopened this ${label}. Rebill it here, finalize, then write the new invoice off — the work stays on the books with a clean paper trail.`
                  : `This ${label} is closed. Create an invoice from it now — it opens as an editable draft you can finalize.`}
              </Text>
              <Box borderWidth="1px" borderRadius="md" p={3}>
                <Stat>
                  <StatLabel>
                    {workOrder?.workOrderNumber || 'Work order'}
                    {workOrder?.customerName ? ` · ${workOrder.customerName}` : ''}
                  </StatLabel>
                  <StatNumber fontSize="2xl">{money(workOrder?.retailTotal)}</StatNumber>
                </Stat>
              </Box>
              <Checkbox isChecked={itemize} onChange={(e) => setItemize(e.target.checked)}>
                Itemize lines (group price)
              </Checkbox>
            </Stack>
          )}
          {/* item 341(a) — assign/review WHO EARNS WHAT while the invoice is still a
              draft, so by finalize-time the split is already set */}
          {step === 1 && (
            <Stack spacing={3}>
              <Text fontSize="sm" color="gray.500">
                Review the commissions this invoice will pay — adjust any rate now (it
                overrides the split for this invoice only), or continue if it looks right.
              </Text>
              <CommissionReviewPanel invoiceId={newInvoiceId} saveRef={panelSaveRef} onBooksOnFinalize={(b) => setBooksNow(b)} />
            </Stack>
          )}
          {step === 2 && (
            <Stack spacing={4}>
              <Text fontSize="sm">
                An invoice was created as an editable
                {' '}
                <b>draft</b>
                . Enter or adjust its lines, then
                {' '}
                <b>Finalize</b>
                {' '}
                it when ready — finalizing locks it permanently and BOOKS the commissions you just reviewed (you&apos;ll see them once more at finalize).
              </Text>
              <Text fontSize="xs" color="gray.500">
                You can re-invoice this work order again later if you need to.
              </Text>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter>
          {step === 0 && (
            <>
              <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isGenerating}>Not now</Button>
              <Button onClick={onGenerate} isLoading={isGenerating}>Generate invoice</Button>
            </>
          )}
          {step === 1 && (
            <>
              <Button variant="ghost" mr={3} onClick={onClose}>Done</Button>
              {/* item 1334 — persist any unsaved rate edits before moving on (WYSIWYG) */}
              <Button
                onClick={async () => {
                  if (panelSaveRef.current && !(await panelSaveRef.current())) return;
                  // item 1470 — nothing books: make the human say so before moving on
                  if (!booksNow && !(await confirmDialog(
                    'Every commission-flagged line on this invoice resolves to nobody (see the reasons above). Finalizing will book NO commissions. Fix the cause first (assign the lead, set the customer rep), or continue anyway?',
                    { title: 'This will book NO commissions', confirmLabel: 'Continue anyway', cancelLabel: 'Go back' },
                  ))) return;
                  setStep(2);
                }}
              >
                Looks right — continue
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="ghost" mr={3} onClick={onClose}>Done</Button>
              {/* item 772 — open the draft only if lines need editing… */}
              <Button variant="outline" mr={3} onClick={openDraft}>Open draft to edit</Button>
              {/* …or finalize (± send) right here, matching the deposit flow */}
              <Button onClick={() => setFinalizeOpen(true)}>Finalize now…</Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
      {/* item 772 (Chad) — the same finalize modal the ledger uses (PO prompt + commission
          review + Finalize / Finalize & send), so both invoice flows end identically. */}
      {newInvoiceId && (
        <FinalizeInvoiceModal
          isOpen={finalizeOpen}
          onClose={() => setFinalizeOpen(false)}
          invoiceId={newInvoiceId}
          showSend={!writeOff}
          onFinalized={(send) => {
            setFinalizeOpen(false);
            if (writeOff) { onFinalizedInvoice?.(newInvoiceId); return; } // item 1583
            if (send) setSendSignal((sig) => sig + 1); // the send dialog takes over
            else onClose();
          }}
        />
      )}
      {/* item 772 — the same branded send dialog the ledger uses */}
      {newInvoiceId && (
        <SendDocumentButton
          hideTrigger
          documentType="Invoice"
          documentId={newInvoiceId}
          autoOpenSignal={sendSignal}
          onSent={onClose}
        />
      )}
    </Modal>
  );
};

WorkOrderInvoiceWizard.defaultProps = {
  workOrder: undefined,
  writeOff: false,
  onFinalizedInvoice: undefined,
};

export default WorkOrderInvoiceWizard;
